import { FC, useState, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { confirmPopup, ConfirmPopup } from 'primereact/confirmpopup';
import { ProgressSpinner } from 'primereact/progressspinner';
import { WaterfallVersionViewModel } from '../../models/waterfall';

interface WaterfallPublicationsDialogProps {
  visible: boolean;
  onClose: () => void;
  versions: WaterfallVersionViewModel[];
  currentVersionId?: number | null;
  onDuplicate: (version: WaterfallVersionViewModel) => void;
  onSetActive: (waterfallVersionId: number) => void;
  onDelete: (waterfallVersionId: number) => Promise<void>;
  versionIdsWithExecutionRecords: number[];
  isLoadingExecutionCheck: boolean;
}

export const WaterfallPublicationsDialog: FC<WaterfallPublicationsDialogProps> = ({
  visible,
  onClose,
  versions,
  currentVersionId,
  onDuplicate,
  onSetActive,
  onDelete,
  versionIdsWithExecutionRecords,
  isLoadingExecutionCheck,
}) => {
  const [isDeleting, setIsDeleting] = useState<number | null>(null);

  const sortedVersions = useMemo(() => {
    return [...versions].sort((a, b) => b.versionNumber - a.versionNumber);
  }, [versions]);

  const handleDelete = async (waterfallVersionId: number) => {
    setIsDeleting(waterfallVersionId);
    try {
      await onDelete(waterfallVersionId);
    } catch {
      // Error is already handled (toast) by the parent callback
    } finally {
      setIsDeleting(null);
    }
  };

  const handleClose = () => {
    if (isDeleting === null) {
      onClose();
    }
  };

  const canDeleteVersion = (version: WaterfallVersionViewModel): boolean => {
    if (version.waterfallVersionId === currentVersionId) return false;
    if (versionIdsWithExecutionRecords.includes(version.waterfallVersionId)) return false;
    return true;
  };


  return (
    <Dialog
      header="Publications"
      visible={visible}
      onHide={handleClose}
      style={{ width: '850px' }}
      dismissableMask={isDeleting === null}
      closable={isDeleting === null}
    >
      <ConfirmPopup />
      <div className="flex flex-column gap-3">
        {isLoadingExecutionCheck ? (
          <div className="flex align-items-center justify-content-center p-4">
            <ProgressSpinner style={{ width: '30px', height: '30px' }} strokeWidth="4" />
            <span className="ml-2 text-sm">Loading version details...</span>
          </div>
        ) : (
          <DataTable
            value={sortedVersions}
            paginator={sortedVersions.length > 10}
            rows={10}
            stripedRows
            emptyMessage="No versions found"
            className="p-datatable-sm"
          >
            <Column
              field="versionNumber"
              header="Version"
              style={{ width: '100px' }}
              sortable
              body={(row: WaterfallVersionViewModel) => (
                <div className="flex align-items-center gap-2">
                  <span>v{row.versionNumber}</span>
                  {row.waterfallVersionId === currentVersionId && (
                    <span
                      className="text-xs font-semibold px-2 py-1 border-round"
                      style={{ backgroundColor: '#D1FAE5', color: '#065F46' }}
                    >
                      Active
                    </span>
                  )}
                </div>
              )}
            />
            <Column
              field="createdDate"
              header="Created"
              sortable
              style={{ minWidth: '180px' }}
              body={(row: WaterfallVersionViewModel) => {
                if (!row.createdDate) return '-';
                const date = new Date(row.createdDate);
                return date.toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                });
              }}
            />
            <Column
              field="notes"
              header="Notes"
              style={{ minWidth: '200px' }}
              body={(row: WaterfallVersionViewModel) => row.notes || '-'}
            />
            <Column
              header="Actions"
              style={{ width: '160px' }}
              body={(row: WaterfallVersionViewModel) => {
                const canDelete = canDeleteVersion(row);
                const isActive = row.waterfallVersionId === currentVersionId;
                return (
                  <div className="flex gap-1">
                    <Button
                      icon="pi pi-clone"
                      className="p-button-text p-button-sm"
                      onClick={() => onDuplicate(row)}
                      tooltip="Duplicate version"
                      tooltipOptions={{ position: 'top' }}
                      disabled={isDeleting !== null}
                    />
                    <Button
                      icon="pi pi-check-circle"
                      className="p-button-text p-button-sm p-button-success"
                      onClick={() => onSetActive(row.waterfallVersionId)}
                      tooltip="Set as active version"
                      tooltipOptions={{ position: 'top' }}
                      disabled={isActive || isDeleting !== null}
                      style={isActive ? { opacity: 0.3 } : {}}
                    />
                    {canDelete && (
                      <Button
                        icon="pi pi-trash"
                        className="p-button-text p-button-sm p-button-danger"
                        onClick={(e) => {
                          confirmPopup({
                            target: e.currentTarget,
                            message: 'Are you sure you want to delete this version?',
                            icon: 'pi pi-exclamation-triangle',
                            acceptClassName: 'p-button-danger p-button-sm',
                            accept: () => handleDelete(row.waterfallVersionId),
                            reject: () => {},
                          });
                        }}
                        disabled={isDeleting !== null}
                        loading={isDeleting === row.waterfallVersionId}
                        tooltip="Delete version"
                        tooltipOptions={{ position: 'top' }}
                      />
                    )}
                  </div>
                );
              }}
            />
          </DataTable>
        )}
      </div>
    </Dialog>
  );
};
